import { useEffect, useRef, useState } from "react";
import { Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { generatePassword } from "./generatePassword";

const iconButtonClass =
  "flex h-8 w-8 items-center justify-center rounded-md text-[#828899] hover:bg-[#f0f0f0] hover:text-[#555] transition-colors";

export const PasswordWithSuggestion = ({
  value,
  onChange,
  onApplySuggestion,
  showSuggestion = false,
  resetKey,
  name = "password",
  inputClassName = "information-data w-full",
}) => {
  const { t } = useTranslation();
  const [secure, setSecure] = useState(true);
  const [suggestion, setSuggestion] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef(null);

  const refreshSuggestion = () => {
    setSuggestion(generatePassword(12));
  };

  useEffect(() => {
    if (showSuggestion) {
      refreshSuggestion();
      setSecure(true);
      setIsFocused(false);
    }
  }, [showSuggestion, resetKey]);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    [],
  );

  const applySuggestion = () => {
    onApplySuggestion(suggestion);
    setSecure(false);
    setIsFocused(false);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    setIsFocused(true);
    if (showSuggestion && !suggestion) {
      refreshSuggestion();
    }
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 150);
  };

  const showSuggestionDropdown =
    showSuggestion && !value && suggestion && isFocused;

  return (
    <div className="w-full">
      <div className="relative">
        <Input
          className={`${inputClassName} !pr-10 ${
            showSuggestionDropdown ? "!rounded-b-none !border-b-0" : ""
          }`}
          type={secure ? "password" : "text"}
          name={name}
          autoComplete="new-password"
          value={value || ""}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <button
          type="button"
          className={`${iconButtonClass} absolute right-1 top-1/2 -translate-y-1/2`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setSecure(!secure)}
          title={secure ? t("Show password") : t("Hide password")}
          aria-label={secure ? t("Show password") : t("Hide password")}
        >
          <i
            className={`fas text-sm ${secure ? "fa-eye-slash" : "fa-eye"}`}
            aria-hidden="true"
          />
        </button>

        {showSuggestionDropdown ? (
          <div
            className="absolute left-0 right-0 top-full z-20 overflow-hidden rounded-b-md border border-t-0 border-[#ced4da] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
            onMouseDown={(e) => e.preventDefault()}
          >
            <button
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-[#f7f8fc] transition-colors"
              onClick={applySuggestion}
            >
              <i
                className="fas fa-key text-[#007a79] text-sm shrink-0"
                aria-hidden="true"
              />
              <span className="flex-1 min-w-0">
                <span className="block text-xs text-[#5e5e5e]">
                  {t("Suggested password")}
                </span>
                <span className="block text-sm font-mono text-[#222222] truncate">
                  {suggestion}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold text-cyan">
                {t("Use suggestion")}
              </span>
            </button>
            <button
              type="button"
              className="w-full border-t border-[#f0f0f0] px-3 py-2 text-left text-xs font-semibold text-[#5e5e5e] hover:bg-[#f7f8fc] hover:text-cyan transition-colors"
              onClick={refreshSuggestion}
            >
              {t("Generate another password")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const PasswordConfirmField = ({
  value,
  onChange,
  name = "passwordConfirm",
  inputClassName = "information-data w-full",
}) => {
  const { t } = useTranslation();
  const [secure, setSecure] = useState(true);

  return (
    <div className="relative w-full">
      <Input
        className={`${inputClassName} !pr-10`}
        type={secure ? "password" : "text"}
        name={name}
        autoComplete="new-password"
        value={value || ""}
        onChange={onChange}
      />
      <button
        type="button"
        className={`${iconButtonClass} absolute right-1 top-1/2 -translate-y-1/2`}
        onClick={() => setSecure(!secure)}
        title={secure ? t("Show password") : t("Hide password")}
        aria-label={secure ? t("Show password") : t("Hide password")}
      >
        <i
          className={`fas text-sm ${secure ? "fa-eye-slash" : "fa-eye"}`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
};
